import React, { useState } from 'react';
import { RadioGroup, Radio } from './RadioGroup';
import { Toggle } from './Toggle';
import { Separator } from './Separator';
import { BellIcon, UserIcon, EyeIcon, FileTextIcon, DownloadIcon, SlackIcon, ArrowLeftIcon, SettingsIcon, PaletteIcon, VolumeIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
interface SettingsProps {
  onBack?: () => void;
}
const Settings: React.FC<SettingsProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('personalization');
  const [fontSize, setFontSize] = useState('medium');
  const [contrast, setContrast] = useState('normal');
  const [toggleStates, setToggleStates] = useState({
    sound: true,
    effects: false,
    reminder: true,
    checkin: true,
    newContent: false,
    progress: true,
    screenReader: false,
    reduceAnimations: false,
    anonymousData: true,
    personalization: true
  });
  const handleDownloadReport = () => {
    alert('Voortgangsrapport wordt gedownload...');
  };
  const handleDeleteAccount = () => {
    if (confirm('Weet je zeker dat je je account wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      alert('Account verwijderingsproces gestart. Je ontvangt een bevestigingsmail.');
    }
  };
  const handleToggleChange = (setting: string, value: boolean) => {
    setToggleStates(prev => ({
      ...prev,
      [setting]: value
    }));
    // Provide feedback
    if (setting === 'reminder' && !value) {
      alert('Je zult geen dagelijkse herinneringen meer ontvangen');
    } else if (setting === 'anonymousData' && !value) {
      alert('Je deelt geen anonieme gebruiksdata meer. Dit helpt ons de app te verbeteren.');
    }
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'personalization':
        return <div className="space-y-8" data-id="element-1030">
            <section data-id="element-1031">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1032">
                Coach-stijl
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1033">
                Hoe wil je dat je coach met je communiceert?
              </p>
              <RadioGroup legend="Coach stijl" defaultValue="motivating" data-id="element-1034">
                <Radio value="motivating" data-id="element-1035">Motiverend</Radio>
                <Radio value="light" data-id="element-1036">Luchtig</Radio>
                <Radio value="serious" data-id="element-1037">Serieus</Radio>
              </RadioGroup>
            </section>
            <Separator data-id="element-1038" />
            <section data-id="element-1039">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1040">
                Voorkeuren
              </h3>
              <div className="space-y-4" data-id="element-1041">
                <div className="flex items-center justify-between" data-id="element-1042">
                  <div data-id="element-1043">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1044">Geluid</p>
                    <p className="text-xs text-gray-500" data-id="element-1045">
                      Achtergrondgeluid tijdens oefeningen
                    </p>
                  </div>
                  <Toggle label="Geluid" labelPosition="left" checked={toggleStates.sound} onChange={value => handleToggleChange('sound', value)} data-id="element-1046" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1047">
                  <div data-id="element-1048">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1049">
                      Visuele effecten
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1050">
                      Subtiele animaties in de app
                    </p>
                  </div>
                  <Toggle label="Effecten" labelPosition="left" checked={toggleStates.effects} onChange={value => handleToggleChange('effects', value)} data-id="element-1051" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1052">
                  <div data-id="element-1053">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1054">
                      Dagelijkse herinnering
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1055">
                      Ontvang een dagelijkse check-in notificatie
                    </p>
                  </div>
                  <Toggle label="Herinnering" labelPosition="left" checked={toggleStates.reminder} onChange={value => handleToggleChange('reminder', value)} data-id="element-1056" />
                </div>
              </div>
            </section>
          </div>;
      case 'notifications':
        return <div className="space-y-8" data-id="element-1057">
            <section data-id="element-1058">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1059">
                Notificatie-instellingen
              </h3>
              <div className="space-y-4" data-id="element-1060">
                <div className="flex items-center justify-between" data-id="element-1061">
                  <div data-id="element-1062">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1063">
                      Check-in herinneringen
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1064">
                      Dagelijkse herinnering om in te checken
                    </p>
                  </div>
                  <Toggle label="Check-in" labelPosition="left" checked={toggleStates.checkin} onChange={value => handleToggleChange('checkin', value)} data-id="element-1065" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1066">
                  <div data-id="element-1067">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1068">
                      Nieuwe oefeningen
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1069">
                      Meldingen over nieuwe content
                    </p>
                  </div>
                  <Toggle label="Nieuwe content" labelPosition="left" checked={toggleStates.newContent} onChange={value => handleToggleChange('newContent', value)} data-id="element-1070" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1071">
                  <div data-id="element-1072">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1073">
                      Voortgangsberichten
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1074">
                      Wekelijkse updates over je voortgang
                    </p>
                  </div>
                  <Toggle label="Voortgang" labelPosition="left" checked={toggleStates.progress} onChange={value => handleToggleChange('progress', value)} data-id="element-1075" />
                </div>
              </div>
            </section>
            <Separator data-id="element-1076" />
            <section data-id="element-1077">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1078">
                Notificatietijden
              </h3>
              <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1079">
                <p className="text-sm font-medium text-gray-800 mb-2" data-id="element-1080">
                  Check-in herinnering
                </p>
                <select className="w-full p-2 border border-gray-300 rounded-md" onChange={() => alert('Notificatietijd aangepast')} data-id="element-1081">
                  <option data-id="element-1082">08:00</option>
                  <option data-id="element-1083">12:00</option>
                  <option data-id="element-1084">18:00</option>
                  <option data-id="element-1085">20:00</option>
                  <option data-id="element-1086">22:00</option>
                </select>
              </div>
            </section>
          </div>;
      case 'accessibility':
        return <div className="space-y-8" data-id="element-1087">
            <section data-id="element-1088">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1089">
                Tekstgrootte
              </h3>
              <div className="space-y-3" data-id="element-1090">
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${fontSize === 'small' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setFontSize('small')} data-id="element-1091">
                  <span className="text-sm" data-id="element-1092">Klein</span>
                  {fontSize === 'small' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1093"></div>}
                </button>
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${fontSize === 'medium' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setFontSize('medium')} data-id="element-1094">
                  <span className="text-base" data-id="element-1095">Normaal</span>
                  {fontSize === 'medium' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1096"></div>}
                </button>
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${fontSize === 'large' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setFontSize('large')} data-id="element-1097">
                  <span className="text-lg" data-id="element-1098">Groot</span>
                  {fontSize === 'large' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1099"></div>}
                </button>
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${fontSize === 'xlarge' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setFontSize('xlarge')} data-id="element-1100">
                  <span className="text-xl" data-id="element-1101">Extra groot</span>
                  {fontSize === 'xlarge' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1102"></div>}
                </button>
              </div>
            </section>
            <Separator data-id="element-1103" />
            <section data-id="element-1104">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1105">
                Contrast
              </h3>
              <div className="space-y-3" data-id="element-1106">
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${contrast === 'normal' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setContrast('normal')} data-id="element-1107">
                  <span data-id="element-1108">Normaal contrast</span>
                  {contrast === 'normal' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1109"></div>}
                </button>
                <button className={`w-full p-3 text-left border rounded-lg flex items-center justify-between ${contrast === 'high' ? 'border-gray-500 bg-gray-100' : 'border-gray-300'}`} onClick={() => setContrast('high')} data-id="element-1110">
                  <span data-id="element-1111">Hoog contrast</span>
                  {contrast === 'high' && <div className="w-4 h-4 rounded-full bg-gray-500" data-id="element-1112"></div>}
                </button>
              </div>
            </section>
            <Separator data-id="element-1113" />
            <section data-id="element-1114">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1115">
                Overige toegankelijkheid
              </h3>
              <div className="space-y-4" data-id="element-1116">
                <div className="flex items-center justify-between" data-id="element-1117">
                  <div data-id="element-1118">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1119">
                      Schermlezer-ondersteuning
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1120">
                      Verbeterde ondersteuning voor schermlezers
                    </p>
                  </div>
                  <Toggle label="Schermlezer" labelPosition="left" checked={toggleStates.screenReader} onChange={value => handleToggleChange('screenReader', value)} data-id="element-1121" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1122">
                  <div data-id="element-1123">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1124">
                      Verminder animaties
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1125">
                      Minimaliseer bewegende elementen
                    </p>
                  </div>
                  <Toggle label="Animaties" labelPosition="left" checked={toggleStates.reduceAnimations} onChange={value => handleToggleChange('reduceAnimations', value)} data-id="element-1126" />
                </div>
              </div>
            </section>
          </div>;
      case 'privacy':
        return <div className="space-y-8" data-id="element-1127">
            <section data-id="element-1128">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1129">
                Gegevens
              </h3>
              <div className="space-y-4" data-id="element-1130">
                <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1131">
                  <p className="text-sm font-medium text-gray-800 mb-1" data-id="element-1132">
                    Data-export
                  </p>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-1133">
                    Download al je gegevens in een bestand
                  </p>
                  <button className="text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 flex items-center" onClick={() => alert('Je gegevens worden geëxporteerd. Je ontvangt een e-mail wanneer het bestand klaar is.')} data-id="element-1134">
                    <DownloadIcon className="w-4 h-4 mr-1" data-id="element-1135" />
                    Exporteren
                  </button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1136">
                  <p className="text-sm font-medium text-gray-800 mb-1" data-id="element-1137">
                    Voortgangsrapport
                  </p>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-1138">
                    Download een samenvatting van je voortgang
                  </p>
                  <button onClick={handleDownloadReport} className="text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 flex items-center" data-id="element-1139">
                    <FileTextIcon className="w-4 h-4 mr-1" data-id="element-1140" />
                    Rapport downloaden
                  </button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg" data-id="element-1141">
                  <p className="text-sm font-medium text-gray-800 mb-1" data-id="element-1142">
                    Account verwijderen
                  </p>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-1143">
                    Verwijder je account en alle gegevens permanent
                  </p>
                  <button className="text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-1 hover:bg-gray-50 flex items-center" onClick={handleDeleteAccount} data-id="element-1144">
                    <AlertTriangleIcon className="w-4 h-4 mr-1" data-id="element-1145" />
                    Account verwijderen
                  </button>
                </div>
              </div>
            </section>
            <Separator data-id="element-1146" />
            <section data-id="element-1147">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1148">
                Privacy-instellingen
              </h3>
              <div className="space-y-4" data-id="element-1149">
                <div className="flex items-center justify-between" data-id="element-1150">
                  <div data-id="element-1151">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1152">
                      Anonieme gebruiksdata
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1153">
                      Help ons de app te verbeteren
                    </p>
                  </div>
                  <Toggle label="Gebruiksdata" labelPosition="left" checked={toggleStates.anonymousData} onChange={value => handleToggleChange('anonymousData', value)} data-id="element-1154" />
                </div>
                <div className="flex items-center justify-between" data-id="element-1155">
                  <div data-id="element-1156">
                    <p className="text-sm font-medium text-gray-800" data-id="element-1157">
                      Personalisatie
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1158">
                      Sta toe dat we content personaliseren
                    </p>
                  </div>
                  <Toggle label="Personalisatie" labelPosition="left" checked={toggleStates.personalization} onChange={value => handleToggleChange('personalization', value)} data-id="element-1159" />
                </div>
              </div>
            </section>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-1160">
      <div className="flex items-center mb-6" data-id="element-1161">
        {onBack && <button onClick={onBack} className="mr-3 p-2 rounded-full hover:bg-gray-100" aria-label="Terug" data-id="element-1162">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1163" />
          </button>}
        <h2 className="text-2xl font-light text-gray-800" data-id="element-1164">Instellingen</h2>
      </div>
      <div className="mb-8 overflow-x-auto" data-id="element-1165">
        <div className="flex border-b border-gray-200 min-w-max" data-id="element-1166">
          <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'personalization' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('personalization')} data-id="element-1167">
            <div className="flex items-center" data-id="element-1168">
              <SettingsIcon className="w-4 h-4 mr-1" data-id="element-1169" />
              <span data-id="element-1170">Personalisatie</span>
            </div>
          </button>
          <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'notifications' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('notifications')} data-id="element-1171">
            <div className="flex items-center" data-id="element-1172">
              <BellIcon className="w-4 h-4 mr-1" data-id="element-1173" />
              <span data-id="element-1174">Notificaties</span>
            </div>
          </button>
          <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'accessibility' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('accessibility')} data-id="element-1175">
            <div className="flex items-center" data-id="element-1176">
              <EyeIcon className="w-4 h-4 mr-1" data-id="element-1177" />
              <span data-id="element-1178">Toegankelijkheid</span>
            </div>
          </button>
          <button className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === 'privacy' ? 'text-gray-800 border-b-2 border-gray-700' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('privacy')} data-id="element-1179">
            <div className="flex items-center" data-id="element-1180">
              <UserIcon className="w-4 h-4 mr-1" data-id="element-1181" />
              <span data-id="element-1182">Privacy</span>
            </div>
          </button>
        </div>
      </div>
      <div data-id="element-1183">{renderTabContent()}</div>
    </div>;
};
export default Settings;